import React, { useContext, useEffect, useState } from 'react';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExtraProduct,
  removeExtraProduct,
  setExtraProductList,
} from '../../../actions/extraProductActions';
import { useTranslation } from 'react-i18next';

import ButtonReservation from '../../../components/Common/Button/ButtonReservation';
import Money from '../../../components/Common/Money/Money';
import { setReservation } from '../../../actions/reservationActions';
import ChevronIcon from '../../../components/icons/chevronIcon';
import styles from '../ReservationCheck/reservationCheck.module.css';
import { CircleSpinner } from 'react-spinners-kit';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import { format } from 'date-fns';
import ProductModal from '../../../components/Common/ProductModal/ProductModal';
import ReservationTicketInfo from '../ReservationTicketInfo/ReservationTicketInfo';
import { fetchProducts } from '../../../services/product/productService';
import EventProductCard from '../../../components/EventProductCard/EventProductCard';
import SkeletonReservationArea from '../../../components/Skeletons/SkeletonReservationArea';
import ProductCard from '../../../components/ProductCard/ProductCard';
import SeatMap from '../../../components/SeatMap/SeatMap';
import CurrencySelectorWithPicker from '../../../components/CurrencySelector/CurrencySelectorWithPicker';

const ReservationTicketSelector = ({ history }) => {
  const { t } = useTranslation();
  const language = useSelector((state) => state.app.language);

  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);

  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [groupProducts, setGroupProducts] = useState([]);
  const extraProducts = useSelector((state) => state.extraProducts.items);

  const [loading, setLoading] = useState(true);
  const [loadingImage, setLoadingImage] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [eventDetail, setEventDetail] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const idReservation = getQueryString('id');
  const showId = getQueryString('showId');
  const eventId = getQueryString('eventId');
  const [productCategoryId] = useState(getQueryString('productCategoryId'));
  const [selectedProductCategory, setSelectedProductCategory] = useState();
  const orderType = ['ADULTS', 'CHILDREN', 'INFANTS', 'ALL'];


  const queryParams = new URLSearchParams({
    eventId: eventId,
    showId: showId,
  });

  if (idReservation !== null && idReservation !== undefined) {
    queryParams.append('id', idReservation);
  }
  let query = `?${queryParams.toString()}`;

  const getEventDetails = async () => {
    try {
      const response = await welletContext.apis.tickets.get(
        `/shows/event/${eventId}?lang=${language}`,
      );
      setEventDetail(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const productCategorySetup = () => {
    if (!eventDetail) return;
    let newSelectedProductCategory = eventDetail.productCategories.find(
      (c) => c.id.toString() === productCategoryId,
    );
    if (!newSelectedProductCategory) {
      newSelectedProductCategory = eventDetail.productCategories[0];
    }
    setSelectedProductCategory(newSelectedProductCategory);
  };

  useEffect(() => {
    getEventDetails();
  }, []);

  useEffect(() => {
    productCategorySetup();
  }, [eventDetail]);

  useEffect(() => {
    if (eventDetail) {
      getProducts(eventDetail.startDate, selectedProductCategory);
    }
  }, [selectedProductCategory]);

  const getProducts = async (startDate) => {
    setLoadingProducts(true);
    setError(null);

    try {
      const result = await fetchProducts(
        welletContext,
        showId,
        startDate,
        language,
        eventId,
        selectedProductCategory.id,
      );

      if (result && result.data) {
        let initializedProducts = result.data.products.map((product) => ({
          ...product,
          quantity: 0,
          disabled: false,
        }));

        const groupByType = initializedProducts.reduce((acc, item) => {
          const key = item.ageRange ? item.ageRange.name : 'ALL';
          if (!acc[key]) {
            acc[key] = {
              ageRangeId: item.ageRange?.name,
              ageRangeLabel: item.ageRange?.nameString,
              items: [],
            };
          }
          acc[key].items.push(item);
          return acc;
        }, {});

        setProducts(initializedProducts);
        setGroupProducts(groupByType);

        dispatch(setExtraProductList(result.data.extraProducts));

        if (initializedProducts.length === 0) setError(t('productsNotAvailables'));
      }
    } catch (error) {
      setError(
        error.response && error.response.data ? t('reservationNotAvailables') : t('callApiError'),
      );
      console.error(error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleIncrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    const product = products[index];

    if (product.stock && product.stock === product.quantity) return;

    let newProducts = [...products];

    if (product.isExclusive) {
      newProducts
        .filter((p) => p.id !== product.id)
        .forEach((p) => {
          p.disabled = true;
          p.quantity = 0;
          dispatch(removeExtraProduct({ ...p, quantity: 0, specialType: 'EXTRAPAX' }));
        });
    }

    newProducts[index].disabled = false;
    newProducts[index].quantity = newProducts[index].stock
      ? Math.min(newProducts[index].stock, newProducts[index].quantity + 1)
      : newProducts[index].quantity + 1;

    setProducts(newProducts);

    if (product.extraPax > 0) {
      dispatch(addExtraProduct({ ...product, quantity: 0, specialType: 'EXTRAPAX' }));
    }

    if (product.behavior === 'NO_GUARANTEED') {
      const reservationInfo = setReservationInfo();
      dispatch(setReservation(reservationInfo));
      history.push({
        pathname: '/reservation-no-guaranteed',
        search: query,
      });
    }

    // uncomment the following block to disable multi product selection
    // if (product.reservationType === 'AREA') {
    //   const reservationInfo = setReservationInfo();
    //   dispatch(setReservation(reservationInfo));

    //   if (product.behavior === 'NO_GUARANTEED')
    //     history.push({
    //       pathname: '/reservation-no-guaranteed',
    //       search: query,
    //     });
    //   else if (product.extraPax > 0 || extraProducts.length > 0) {
    //     linkToExtraProducts();
    //   } else {
    //     handleContinue();
    //   }
    // }
  };

  const handleDecrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    let newProducts = [...products];

    newProducts[index].quantity = Math.max(0, newProducts[index].quantity - 1);

    if (newProducts[index].isExclusive && newProducts[index].quantity === 0) {
      newProducts.forEach((p) => (p.disabled = false));
    }
    dispatch(removeExtraProduct({ ...newProducts[index], quantity: 0, specialType: 'EXTRAPAX' }));
    setProducts(newProducts);
  };

  const handleCategoryClick = (type) => {
    setSelectedProductCategory(type);
    setLoadingImage(true);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const onInfoClick = (id) => {
    setSelectedProduct(id);
    toggleModal();
  };

  const handleContinue = () => {
    const reservationInfo = setReservationInfo();
    dispatch(setReservation(reservationInfo));

    if (extraProducts.length > 0) {
      linkToExtraProducts();
    } else if (eventDetail.hasTransportation) {
      history.push({
        pathname: '/pickup',
        search: query,
      });
    } else {
      history.push({
        pathname: '/reservation-check-tickets',
        search: query,
      });
    }
  };

  const linkToExtraProducts = () => {
    history.push({
      pathname: '/area-extra-reservation',
      search: query,
    });
  };

  const setReservationInfo = () => {
    let finalProducts = [];

    products
      .filter((product) => product.quantity > 0)
      .map((product) => {
        finalProducts.push({
          id: product.id,
          quantity: product.quantity,
          extraPaxs: 0,
          specialType: product.specialType,
          paxs: product.pax * product.quantity,
        });
      });

    const reservationInfo = {
      showId: showId,
      showName: eventDetail.name,
      products: finalProducts,
      date: format(new Date(eventDetail.startDate), 'yyyy-MM-dd'),
      hasTransportation: eventDetail.hasTransportation,
      performance: format(new Date(eventDetail.startDate), 'HH:mm'),
      paxs: finalProducts.reduce((sum, item) => sum + item.paxs, 0),
    };

    return reservationInfo;
  };

  const sumTotal = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.price * p.quantity, 0);
  };

  const totalConsume = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.consumption * p.quantity, 0);
  };

  const totalPax = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.pax * p.quantity, 0);
  };

  const renderProducts = () => {
    if (loading || loadingProducts) {
      return SkeletonReservationArea();
    }

    if (selectedProductCategory.seatMap) {
      return (
        <SeatMap
          seatMapConfig={selectedProductCategory.seatMap}
          products={products}
          eventDetail={eventDetail}
          showId={showId}
          eventId={eventId}
        />
      );
    }

    return (
      <div className='product-list mt-3'>
        {orderType.map((type, i) => {
          const productItems = groupProducts[type];
          if (productItems) {
            return (
              <div className='product-types' key={i}>
                {type !== 'ALL' && (
                  <div className='fw-600 text-primary pl-1'>{productItems.ageRangeLabel}</div>
                )}
                <div className='mt-2'>
                  {productItems.items.map((p) => (
                    <div className='mt-2' key={p.id}>
                      {p.behavior === 'NO_GUARANTEED' ? (
                        <EventProductCard
                          product={p}
                          handleIncrement={() => handleIncrement(p.id)}
                          handleDecrement={() => handleDecrement(p.id)}
                          hasMoreInfo={false}
                        />
                      ) : p.reservationType === 'AREA' ? (
                        <ProductCard
                          product={p}
                          handleIncrement={() => handleIncrement(p.id)}
                          handleDecrement={() => handleDecrement(p.id)}
                          hasMoreInfo={true}
                          onInfoClick={() => {
                            onInfoClick(p);
                          }}
                          loadingImage={loadingImage}
                          setLoadingImage={setLoadingImage}
                        />
                      ) : (
                        <ProductCard
                          product={p}
                          handleIncrement={() => handleIncrement(p.id)}
                          handleDecrement={() => handleDecrement(p.id)}
                          hasMoreInfo={true}
                          onInfoClick={() => {
                            onInfoClick(p);
                          }}
                          loadingImage={loadingImage}
                          setLoadingImage={setLoadingImage}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const hasItems = products?.some((p) => p.quantity > 0);
  const reservationType = hasItems ? products[0].reservationType : null;

  return (
    <>
      {loading ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header />
          <ContentApp>
            <EventHeader
              eventImg={eventDetail.bannerImage}
              eventName={eventDetail.name}
              eventDescription={eventDetail.show.name}
              eventDate={eventDetail.startDate}
              eventEndDate={eventDetail.endDate}
            />

            {selectedProductCategory && (
              <div className='row text-center font-normal2 mt-4 px-3'>
                {eventDetail.productCategories.map((category) => (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className={`col p-2 ${
                      selectedProductCategory.id === category.id ? 'bold' : 'text-tertiary'
                    }`}
                    style={{
                      borderBottom:
                        selectedProductCategory.id === category.id
                          ? '4px solid var(--color-primary)'
                          : '1px solid #7E7E7E',
                    }}
                  >
                    {category.name}
                  </div>
                ))}
              </div>
            )}

            <div className='mt-3' style={{ paddingBottom: '115px' }}>
              <>
                {renderProducts()}
                <div className='mt-3'>
                  <CurrencySelectorWithPicker />
                </div>
              </>
            </div>

            {hasItems && (
              <div
                className='position-fixed w-100'
                style={{
                  bottom: 0,
                  left: 0,
                  zIndex: 2,
                }}
              >
                <div className='container'>
                  <div className='row justify-content-center'>
                    <div className='col-12 col-md-7 col-lg-6 p-0'>
                      <ButtonReservation
                        leftContent={
                          <>
                            <div className='font-normal'>
                              {t('total')} <Money value={sumTotal()} />
                            </div>
                            <div className='font-smaller'>
                              {reservationType.toUpperCase() === 'AREA'
                                ? t('upToGuests', { pax: totalPax() })
                                : t('person', { count: totalPax() })}
                            </div>
                            {totalConsume() > 0 && (
                              <div className='font-smaller'>
                                {t('consumptionCredit')} <Money value={totalConsume()} />
                              </div>
                            )}
                          </>
                        }
                        rightContent={
                          <div>
                            {t('continue')}
                            <span>
                              <ChevronIcon size={20} fillColor={'#000'} />
                            </span>
                          </div>
                        }
                        size='large'
                        className={`${styles.btn}`}
                        onClick={handleContinue}
                        fixed={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* {reservationType === 'AREA' && (
              <div className='mt-4  font-tiny'>
                <div className='bold'>{t('minimumSpend.title')}</div>
                <div className='mt-1 font-smaller'>{t('minimumSpend.text')}</div>
              </div>
            )} */}
          </ContentApp>
        </>
      )}
      <ProductModal
        open={isModalOpen}
        onClose={toggleModal}
        animationClass='slide-left-right'
        fullScreen={true}
        hasBackground={true}
        backIcon={true}
      >
        <ReservationTicketInfo
          productId={selectedProduct?.id}
          showAddButton={
            selectedProduct?.stock === null ||
            (!selectedProduct?.hasSoldOut && selectedProduct?.stock > 0)
          }
          handleIncrement={handleIncrement}
          prices={selectedProduct?.prices}
          toggleModal={toggleModal}
          isModalOpen={isModalOpen}
        />
      </ProductModal>
    </>
  );
};

export default ReservationTicketSelector;
